/**
 * SQL query history for the database viewer — device-local, per identity.
 *
 * ── Why it is not on `users.settings` ───────────────────────────────────
 *
 * The obvious home, and the wrong one, for two reasons. `users` is read on
 * authenticated requests, so a growing array of query text would ride along on
 * every one of them; and sharing one JSONB blob with the theme means a rapid
 * history write can clobber a concurrent theme write. It also is not the kind
 * of thing that should follow a person between machines: it is a scratch pad.
 *
 * (It used to be BOTH — localStorage plus a fire-and-forget PUT to
 * `/api/settings/sql-history`, which wrote a file into the user's own Drive.
 * That file is one of the two writes issue #369 removes so an identity-only
 * tenant needs no drive writes at all.)
 *
 * ── The key is namespaced, because localStorage is per-DEVICE ───────────
 *
 * Not per-account. Two people signing in on one browser, or one person
 * switching accounts, would otherwise read each other's history — and a query
 * carries table names, column names and literal values from a drive the other
 * person may not be able to open. The namespace is derived from the acting
 * identity, so it is impossible for the wrong account to read the right key.
 *
 * Preferred over clearing on sign-out: a missed sign-out path (tab close,
 * session expiry, token revocation, a crash) leaves the data behind, whereas a
 * namespaced key can never be read by the wrong account at all.
 */
import { getActiveTenantId, getActiveOrgEmail } from '@papan/api-client'

export interface HistoryEntry {
  sql: string
  fileName: string
  fileId: string
  timestamp: number
}

const KEY_PREFIX = 'papan:sql-history'

/**
 * The pre-#369 key, unnamespaced and therefore readable by whoever signs in
 * next. Removed on first load rather than migrated: the history is cleared at
 * cutover by agreement, and leaving it behind would keep exactly the
 * cross-account disclosure this module exists to close.
 */
const LEGACY_KEY = 'papan:sql-history'

export const MAX_HISTORY = 100

/** Newest N kept when a write is refused for space — see `save`. */
const SALVAGE_HISTORY = 20

/**
 * A stable, short separator for the acting identity.
 *
 * ── What it is, and what it is NOT ──────────────────────────────────────
 *
 * A SEPARATOR, not a secret. `djb2` is not cryptographic and is not pretending
 * to be: an attacker with the device does not need to reverse the key, they
 * can read the value. Hashing buys one specific thing — the key NAME does not
 * print a colleague's email address into a store the other account can
 * enumerate, which would leak an identity even while the history itself stayed
 * correctly separated.
 *
 * ── Why tenant AND email ────────────────────────────────────────────────
 *
 * There is no user-id getter in `@papan/api-client`, and adding one would mean
 * wiring through `apps/web`. The pair is sufficient and is checked case by
 * case rather than assumed:
 *
 *  - **personal drive** — `tenantId` IS the personal tenant, which is
 *    one-per-account, so the tenant alone separates two people.
 *  - **organization** — the tenant is SHARED, and this is the case the tenant
 *    alone gets wrong: two colleagues on one browser would land on one key.
 *    The acting org email is what separates them.
 *  - **anonymous share view** — neither is set, so every anonymous visitor on
 *    a device shares one bucket. No account's data is exposed to another
 *    account by that, because a signed-in session never writes there.
 *
 * A consequence worth stating: history is now per (identity, tenant) rather
 * than global across tenants. That is a narrowing, and the right one — one
 * tenant's table and column names have no business appearing in a list
 * rendered while acting in another.
 */
function identityKey(): string {
  const raw = `${getActiveTenantId() ?? ''} ${getActiveOrgEmail() ?? ''}`
  let h = 5381
  for (let i = 0; i < raw.length; i++) h = (((h << 5) + h) ^ raw.charCodeAt(i)) >>> 0
  return h.toString(36)
}

/** Exported for tests — the key this device/identity reads and writes. */
export function historyStorageKey(): string {
  return `${KEY_PREFIX}:${identityKey()}`
}

/**
 * Shape-check one parsed entry.
 *
 * The value is user-editable text in a store anyone with the device can edit,
 * so a malformed or hand-edited blob must degrade to an empty history rather
 * than throw on load and take the whole SQL panel with it. Checked per ENTRY
 * rather than per blob, so one bad row does not discard a valid history.
 */
function isEntry(v: unknown): v is HistoryEntry {
  if (typeof v !== 'object' || v === null) return false
  const e = v as Record<string, unknown>
  return typeof e.sql === 'string'
    && typeof e.fileName === 'string'
    && typeof e.fileId === 'string'
    && typeof e.timestamp === 'number'
    && Number.isFinite(e.timestamp)
}

/**
 * Write, failing silently.
 *
 * Storage is blocked outright in some private-browsing modes and can be full
 * in any of them, exactly as `writeLocalTheme` already handles — a scratch pad
 * must never be able to break the query that produced it.
 *
 * One retry with a much shorter list before giving up: a single very large
 * query would otherwise poison every future write, since the failing entry
 * stays at the head of the list forever.
 */
function save(entries: HistoryEntry[]): void {
  const capped = entries.slice(0, MAX_HISTORY)
  try {
    localStorage.setItem(historyStorageKey(), JSON.stringify(capped))
  } catch {
    try {
      localStorage.setItem(historyStorageKey(), JSON.stringify(capped.slice(0, SALVAGE_HISTORY)))
    } catch { /* blocked or still too large — the history is not worth more than this */ }
  }
}

/** Read this identity's history, and reap the pre-#369 shared key. */
export function loadHistory(): HistoryEntry[] {
  try {
    localStorage.removeItem(LEGACY_KEY)
  } catch { /* storage blocked */ }
  try {
    const raw = localStorage.getItem(historyStorageKey())
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isEntry).slice(0, MAX_HISTORY)
  } catch {
    return []
  }
}

/** Prepend an entry, de-duplicating the same SQL against the same file. */
export function addToHistory(entries: HistoryEntry[], entry: HistoryEntry): HistoryEntry[] {
  const filtered = entries.filter((e) => e.sql !== entry.sql || e.fileId !== entry.fileId)
  const updated = [entry, ...filtered].slice(0, MAX_HISTORY)
  save(updated)
  return updated
}
